import { useWorkspaces, WorkspacesState } from './workspaces';
import { useAllWorkspaceFiles, WorkspaceFile, WorkspaceFilesState } from './workspace-files';
import { effectiveContent, parseFileRecordContent } from './file-record';

// The Your Agents cross-workspace Files view's data: every file across every
// top-level workspace, joined client-side to the workspace roster for the names and slugs the file
// rows display.
//
// It reads the SAME endpoint the workspace's own gallery reads (`/api/workspace-files`), just without
// the `workspaceId` filter: the route already loads the whole file-system map and then throws away
// everything outside one prefix, so the account-wide answer is ONE request rather than one per Space.
//
// The cost of the change, stated plainly: the two views no longer share a cache ENTRY (the gallery
// still keys by workspace id), and per-workspace error tolerance is gone — one failed read is now the
// whole view's error rather than one Space silently contributing nothing. Both are the honest shape
// for a single call, and the same file-system duplexer refreshes both entries, so the two views still
// cannot drift apart in practice.

export interface AgentWorkspaceFileItem extends WorkspaceFile {
  /** The Space this file belongs to — absent for a file that belongs to the ACCOUNT rather than to one
   *  Space. A skill is the case that exists: it lives in the file-system's own SKILLS directory, so it has
   *  no Space to name, no Space page to link to, and no Space badge to draw. Every reader of these three
   *  must cope with their absence rather than assume a Space. */
  workspaceId?: string;
  workspaceName?: string;
  workspaceSlug?: string;
  /** From the file's FileRecord header, else from the writer that recorded it (see {@link ownerKindOf}). */
  ownerKind: 'user' | 'agent' | 'workspace';
}

export type AgentWorkspaceFilesState =
  | { status: 'loading' }
  | { status: 'ready'; items: AgentWorkspaceFileItem[] }
  | { status: 'error' };

/** The owner bucket for a single file: its FileRecord header when the body carries one, else the
 *  writer that recorded it. Pure + unit-testable.
 *
 *  The writer is the more durable of the two — a file stored by reference has no body to carry a
 *  header, and an upload is the user's however an agent came to file it away. The header stays first
 *  only because the files written before the writer was recorded have nothing else to go on.
 *  Shared 1:1 with native `FileAttribution.ownerKind`. */
export function ownerKindOf(item: WorkspaceFile): 'user' | 'agent' | 'workspace' {
  const { header } = parseFileRecordContent(effectiveContent(item));
  if (header?.ownerKind === 'user' || header?.ownerKind === 'agent') {
    return header.ownerKind;
  }
  if (item.writerType === 'USER') {
    return 'user';
  }
  return item.writerType ? 'agent' : 'workspace';
}

/** Pure composition of the workspace roster with the one account-wide file read, split out so the
 *  join rules are unit-testable without rendering the hook. */
export function combineAgentWorkspaceFiles(workspacesState: WorkspacesState, filesState: WorkspaceFilesState): AgentWorkspaceFilesState {
  if (workspacesState.status === 'error' || filesState.status === 'error') return { status: 'error' };
  // Neither half is usable alone: without the roster a file has no Space name to file itself under,
  // and without the files the page would render as genuinely empty for a beat.
  if (workspacesState.status === 'loading' || filesState.status === 'loading') return { status: 'loading' };

  const byId = new Map(workspacesState.workspaces.map((workspace) => [workspace.id, workspace]));

  // A file whose Space is not on the roster is DROPPED rather than shown under a blank name — the
  // account-wide read walks the whole file-system map, which can still hold keys for a Space the
  // reader no longer has. Apps carry no content to open/share, so this
  // view (files only) drops them too.
  const items: AgentWorkspaceFileItem[] = [];
  for (const file of filesState.items) {
    if (file.isApp) continue;
    // A file with NO Space is an account-level one — a skill, named `skills/<path>` by the route. It has no
    // roster entry to look up and must not be dropped for lacking one; only a file naming a Space the reader
    // no longer has is dropped.
    const workspace = file.workspaceId ? byId.get(file.workspaceId) : undefined;
    if (file.workspaceId && !workspace) continue;
    items.push({
      ...file,
      workspaceId: workspace?.id,
      workspaceName: workspace?.name,
      workspaceSlug: workspace?.slug,
      ownerKind: ownerKindOf(file),
    });
  }

  return { status: 'ready', items };
}

/** `enabled: false` is for a caller that mounts in a presentation with no file data to show — it must
 *  not pay for a read it will not render. The state then stays `loading`. */
export function useAgentWorkspaceFiles(options?: { enabled?: boolean }): AgentWorkspaceFilesState {
  return combineAgentWorkspaceFiles(useWorkspaces(), useAllWorkspaceFiles(options));
}

